/**
 * Single source of truth for all syntax highlighting colors.
 *
 * These constants are consumed by:
 *  - Monaco editor themes (main playground editor + blog ScadBlock editors)
 *  - Shiki themes (MDX blog post fenced code blocks via rehype-pretty-code)
 *
 * When changing a color here it will propagate to all three surfaces
 * automatically after the next build.
 *
 * Color naming convention: DARK_ / LIGHT_ prefix, then the semantic role.
 */

// ---------------------------------------------------------------------------
// Dark theme token colors
// ---------------------------------------------------------------------------

export const DARK_COMMENT = '#666666';
export const DARK_KEYWORD = '#85ADE7';
export const DARK_IDENTIFIER = '#FF606A';
/** Strings and numbers share the same color */
export const DARK_STRING = '#A1B37A';
export const DARK_NUMBER = '#A1B37A';
/** Delimiters, operators, and brackets share the same color */
export const DARK_DELIMITER = '#eeeeee';
export const DARK_OPERATOR = '#eeeeee';
export const DARK_BRACKET = '#eeeeee';

// Dark theme editor UI colors
export const DARK_BG = '#121212';
export const DARK_FG = '#cccccc';
export const DARK_LINE_HIGHLIGHT = '#2f3337';
export const DARK_LINE_NUMBER = '#858585';
export const DARK_SELECTION = '#264f78';
export const DARK_SELECTION_INACTIVE = '#3a3d41';
export const DARK_BRACKET_HIGHLIGHT = '#ffffff';

// ---------------------------------------------------------------------------
// Light theme token colors
// ---------------------------------------------------------------------------

export const LIGHT_COMMENT = '#6A9955';
export const LIGHT_KEYWORD = '#0000FF';
export const LIGHT_IDENTIFIER = '#001080';
export const LIGHT_STRING = '#A31515';
export const LIGHT_NUMBER = '#098658';
export const LIGHT_DELIMITER = '#000000';
export const LIGHT_OPERATOR = '#000000';
export const LIGHT_BRACKET = '#000000';

// Light theme editor UI colors
export const LIGHT_BG = '#ffffff';
export const LIGHT_FG = '#000000';
export const LIGHT_LINE_HIGHLIGHT = '#f5f5f5';
export const LIGHT_LINE_NUMBER = '#237893';
export const LIGHT_SELECTION = '#add6ff';
export const LIGHT_SELECTION_INACTIVE = '#e5ebf1';

// ---------------------------------------------------------------------------
// Shiki / TextMate theme objects
//
// rehype-pretty-code accepts a ThemeRegistration object (same shape as a
// VS Code theme JSON). We map our Monaco token names to their TextMate
// scope equivalents so fenced code blocks in MDX posts match the editor.
//
// For languages other than OpenSCAD (JavaScript, shell, etc.) we fall back
// to sensible mappings that keep the overall palette consistent.
// ---------------------------------------------------------------------------

type TokenColor = {
  scope: string | string[];
  settings: { foreground?: string; fontStyle?: string };
};

function buildTokenColors(colors: {
  comment: string;
  keyword: string;
  identifier: string;
  string: string;
  number: string;
  delimiter: string;
  operator: string;
  bracket: string;
}): TokenColor[] {
  return [
    // Comments
    {
      scope: ['comment', 'punctuation.definition.comment'],
      settings: { foreground: colors.comment, fontStyle: 'italic' },
    },
    // Keywords — control flow, storage modifiers, built-in language words
    {
      scope: [
        'keyword',
        'keyword.control',
        'keyword.other',
        'storage',
        'storage.type',
        'storage.modifier',
      ],
      settings: { foreground: colors.keyword },
    },
    // Function / module names (calls and definitions)
    {
      scope: [
        'entity.name.function',
        'variable.function',
        'support.function',
        'meta.function-call',
      ],
      settings: { foreground: colors.keyword },
    },
    // Identifiers / variables
    {
      scope: [
        'variable',
        'variable.other',
        'variable.other.readwrite',
        'entity.name.variable',
        'meta.definition.variable.name',
      ],
      settings: { foreground: colors.identifier },
    },
    // Strings
    {
      scope: ['string', 'string.quoted', 'string.template'],
      settings: { foreground: colors.string },
    },
    // Numbers and numeric constants
    {
      scope: ['constant.numeric', 'constant.language', 'constant'],
      settings: { foreground: colors.number },
    },
    // Operators
    {
      scope: ['keyword.operator', 'punctuation.accessor'],
      settings: { foreground: colors.operator },
    },
    // Delimiters and punctuation
    {
      scope: [
        'punctuation',
        'punctuation.separator',
        'punctuation.terminator',
        'punctuation.definition',
        'delimiter',
        'meta.brace',
      ],
      settings: { foreground: colors.delimiter },
    },
    // Brackets
    {
      scope: [
        'punctuation.section',
        'punctuation.definition.block',
        'meta.brace.round',
        'meta.brace.square',
        'meta.brace.curly',
      ],
      settings: { foreground: colors.bracket },
    },
    // Type names, class names, namespaces
    {
      scope: [
        'entity.name.type',
        'entity.name.class',
        'entity.name.namespace',
        'support.type',
        'support.class',
      ],
      settings: { foreground: colors.identifier },
    },
  ];
}

export type ShikiTheme = {
  name: string;
  displayName: string;
  type: 'dark' | 'light';
  colors: Record<string, string>;
  tokenColors: TokenColor[];
};

export const PLAYGROUND_DARK_THEME: ShikiTheme = {
  name: 'playground-dark',
  displayName: 'Playground Dark',
  type: 'dark',
  colors: {
    'editor.background': DARK_BG,
    // Use identifier color as foreground fallback so unrecognised tokens in
    // Shiki match Monaco's behaviour (every identifier gets DARK_IDENTIFIER).
    'editor.foreground': DARK_IDENTIFIER,
    'editor.lineHighlightBackground': DARK_LINE_HIGHLIGHT,
    'editorLineNumber.foreground': DARK_LINE_NUMBER,
    'editor.selectionBackground': DARK_SELECTION,
    'editor.inactiveSelectionBackground': DARK_SELECTION_INACTIVE,
  },
  tokenColors: buildTokenColors({
    comment: DARK_COMMENT,
    keyword: DARK_KEYWORD,
    identifier: DARK_IDENTIFIER,
    string: DARK_STRING,
    number: DARK_NUMBER,
    delimiter: DARK_DELIMITER,
    operator: DARK_OPERATOR,
    bracket: DARK_BRACKET,
  }),
};

export const PLAYGROUND_LIGHT_THEME: ShikiTheme = {
  name: 'playground-light',
  displayName: 'Playground Light',
  type: 'light',
  colors: {
    'editor.background': LIGHT_BG,
    'editor.foreground': LIGHT_IDENTIFIER,
    'editor.lineHighlightBackground': LIGHT_LINE_HIGHLIGHT,
    'editorLineNumber.foreground': LIGHT_LINE_NUMBER,
    'editor.selectionBackground': LIGHT_SELECTION,
    'editor.inactiveSelectionBackground': LIGHT_SELECTION_INACTIVE,
  },
  tokenColors: buildTokenColors({
    comment: LIGHT_COMMENT,
    keyword: LIGHT_KEYWORD,
    identifier: LIGHT_IDENTIFIER,
    string: LIGHT_STRING,
    number: LIGHT_NUMBER,
    delimiter: LIGHT_DELIMITER,
    operator: LIGHT_OPERATOR,
    bracket: LIGHT_BRACKET,
  }),
};
